"use client";

import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import {
  useState, useEffect, useMemo, useContext, createContext,
} from "react";
import { TFunction } from "i18next";

import { dayjsifyPlanning, estimateSlottedItemStart } from "@audacia-hq/shared/utils";
import { Alert } from "@audacia-hq/shared/components";

import { GET_USER_QUEUES } from "../graphql/query";
import { AnonymousQueueItem, MemberQueueInfo } from "../models/consultation";
import { useLazyFetcher } from "../hooks/useClientFetcher";
import { dropQueue, editQueue } from "../services/api";
import { Expert } from "../models/expert";

import { useWS, WSMessage } from "./WSContext";
import { useAuth } from "./AuthContext";
import { useMember } from "./MemberContext";

interface ExpertUidQueueMap<T> {
  [expertUid: string]: T;
}

interface MemberQueueItemUdpdate {
  dropAt?: string;
}

interface QueueContextType {
  memberQueues: ExpertUidQueueMap<MemberQueueInfo>;
  rawMemberQueues: ExpertUidQueueMap<MemberQueueInfo>;
  loading: boolean
  editQueueItem: (queueUid: string, queueItemUid: string, update: MemberQueueItemUdpdate, onSuccess?, onError?) => void;
  dropQueueItem: (queueUid: string, queueItemUid: string, onSuccess?, onError?) => void;
}

const QueueContext = createContext<QueueContextType>({
  memberQueues: {},
  rawMemberQueues: {},
  loading: true,
  editQueueItem: () => undefined,
  dropQueueItem: () => undefined,
});

const QueueProvider: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  const { user } = useAuth();
  const { userQueues: initialUserQueues } = useMember();
  const [getUserQueues, { loading }] = useLazyFetcher<{ userQueues: MemberQueueInfo[] }>(GET_USER_QUEUES, { authenticated: true });
  const ws = useWS();
  const [memberQueues, setMemberQueues] = useState<ExpertUidQueueMap<MemberQueueInfo>>({});
  const [maskedQueues, setMaskedQueues] = useState<ExpertUidQueueMap<MemberQueueInfo>>({});

  const getQueues = () => {
    getUserQueues().then((res) => {
      if (!res.data?.userQueues) return;
      setMemberQueues(res.data.userQueues.reduce((res, qi) => ({ ...res, [qi.targetUid]: qi }), {}));
    });
  };

  useEffect(() => {
    if (initialUserQueues && (!memberQueues || Object.entries(memberQueues).length === 0)) {
      setMemberQueues(initialUserQueues.reduce((res, qi) => ({ ...res, [qi.targetUid]: qi }), {}));
    }
  }, [initialUserQueues]);

  useEffect(() => {
    const sub = ws.subscribe((msg) => handleQueueEvents(msg as WSMessage));
    return () => sub.unsubscribe();
  }, [ws]);

  useEffect(() => {
    if (!user) {
      setMemberQueues({});
      setMaskedQueues({});
      return;
    }
    if (!initialUserQueues) {
      getQueues();
    }
  }, [user]);

  useEffect(() => {
    setMaskedQueues(Object.entries(memberQueues)
      .reduce((aggr, [expertUid, queueInfo]) => {
        if (queueInfo.metadata.isEphemeral) return aggr;
        if (queueInfo.items.length === 1 && queueInfo.items[0].startedAt) return aggr;
        return { ...aggr, [expertUid]: queueInfo };
      }, {}));
  }, [memberQueues]);

  const handleQueueEvents = (msg: WSMessage) => {
    if (msg.event === "ws.reconnected") {
      // this will update data that will then trigger other useEffects to run
      getQueues();
    } else if (msg.event === "member.waitingRoomPosition.updated") {
      const payload = JSON.parse(msg.payload);
      setMemberQueues((prev) => ({ ...prev, [payload.targetUid]: payload }));
    } else if (msg.event === "member.waitingRoomPosition.removed") {
      const payload = JSON.parse(msg.payload);
      setMemberQueues((prev) => Object.values(prev)
        .filter((q) => q.targetUid !== payload.targetUid)
        .reduce((res, qi) => ({ ...res, [qi.targetUid]: qi }), {}));
    }
  };

  const editQueueItem = async (queueUid: string, queueItemUid: string, update, onSuccess = () => undefined, onError = () => undefined) => {
    const { error } = await editQueue({
      variables: {
        queueUid,
        queueItemUid,
        update,
      },
    });
    if (error) {
      onError();
    } else {
      setMemberQueues((prev) => Object.entries(prev).reduce((aggr, [k, v]) => {
        if (v.queueItemUid !== queueItemUid) return { ...aggr, [k]: v };
        return { ...aggr, [k]: { ...v, ...update } };
      }, {}));
      onSuccess();
    }
  };

  const dropQueueItem = async (queueUid: string, queueItemUid: string, onSuccess = () => undefined, onError = () => undefined) => {
    const { error } = await dropQueue({
      variables: {
        queueUid,
        queueItemUid,
      },
    });
    if (error) {
      onError();
    } else {
      setMemberQueues((prev) => Object.values(prev)
        .filter((q) => q.queueUid !== queueUid)
        .reduce((res, qi) => ({ ...res, [qi.targetUid]: qi }), {}));
      onSuccess();
    }
  };

  const value = useMemo(
    () => ({
      memberQueues: maskedQueues, rawMemberQueues: memberQueues, loading, editQueueItem, dropQueueItem,
    }),
    [maskedQueues, memberQueues, loading, editQueueItem, dropQueueItem],
  );

  return (
    <QueueContext.Provider value={value}>
      {children}
    </QueueContext.Provider>
  );
};

const useQueue = () => useContext(QueueContext);

const getBoundSlots = (startFrom: Dayjs, planning) => {
  const dayjsPlanning = dayjsifyPlanning(planning);
  const futureQueueSlots = dayjsPlanning?.queueSlots?.filter((s) => s.end.isAfter(startFrom)) || [];
  const { nightStart } = futureQueueSlots.reduce((aggr, ts) => {
    if (aggr.nightStart) return aggr;
    if (!aggr.lastEnd) return { lastEnd: ts.end, nightStart: !ts.start.isBefore(startFrom) ? startFrom : null };
    return { lastEnd: ts.end, nightStart: aggr.lastEnd.isSame(ts.start) ? null : aggr.lastEnd };
  }, { lastEnd: null, nightStart: null });

  return dayjsPlanning?.slots?.filter((s) => s.end.isAfter(startFrom) && !nightStart?.isBefore(s.end)) || [];
};

const estimateQueueItemStart = (queueItems: AnonymousQueueItem[], planning) => {
  const now = dayjs();
  const boundSlots = getBoundSlots(now, planning);
  return {
    items: estimateSlottedItemStart(queueItems || [], boundSlots, {
      timeBetween: 120,
      minimumOverlap: 300,
      maximumExcess: 900,
      startFrom: now,
    }),
    bounds: boundSlots,
  };
};

const getLastEstimatedItemAndSlot = (expert: Expert, at = -1) => {
  const estimatedItemsAndSlots = estimateQueueItemStart(
    [
      ...(expert?.queue?.items || []),
      {
        position: 9999, duration: 480, startedAt: null, type: "PHONE",
      },
    ],
    expert.planning,
  );
  return { item: estimatedItemsAndSlots.items.at(at), slot: estimatedItemsAndSlots.bounds.at(-1) };
};

const renderLatenessWarning = (lateness: number, t: TFunction) => {
  if (!lateness) return <></>;
  if (lateness > 7200) return <Alert type="alert">{t("queue.lateness.extreme")}</Alert>;
  if (lateness > 3600) return <Alert type="alert">{t("queue.lateness.medium")}</Alert>;
  if (lateness > 600) return <Alert type="alert">{t("queue.lateness.low")}</Alert>;
  return <></>;
};

export default QueueContext;
export {
  QueueProvider, useQueue, estimateQueueItemStart, renderLatenessWarning, getLastEstimatedItemAndSlot,
};
